import { memo, useLayoutEffect, useRef, type MutableRefObject, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, CheckCheck, Lock, Swords, UserRound } from 'lucide-react';

import { UserAvatar } from '@/components/social/UserAvatar';
import { cn } from '@/lib/cn';
import { giornoRiga } from '@/lib/qsocials/dm';
import {
  azioniRiga,
  eSwipe,
  esitoGesto,
  LARGHEZZA_AZIONE_PX,
  latoDi,
  posizioneAperta,
  principale,
  spostamentoConsentito,
  FRAZIONE_LUNGO,
  type AzioneRiga,
  type LatoSwipe,
} from '@/lib/qsocials/swipeRiga';
import type { DmConversation } from '@/types/dm';
import { linguaFormato } from '@/lib/fmt';
import { OSCURA } from '@/lib/v3';

const TRANSIZIONE = 'transform 220ms ease';

const ICONA: Record<AzioneRiga, typeof Ban> = { letto: CheckCheck, sfida: Swords, profilo: UserRound, blocca: Ban };
const COLORE: Record<AzioneRiga, string> = {
  letto: 'bg-[#0f9d8f]',
  sfida: 'bg-[#c9861c]',
  profilo: 'bg-[#2f7fd1]',
  blocca: 'bg-danger',
};

/** L'ora a destra della riga: oggi l'ora, ieri «Ieri», prima il giorno (`giornoRiga`). */
function oraRiga(ts: string, lingua: string, ieri: string): string {
  const giorno = giornoRiga(ts, new Date());
  if (giorno === null) return '';
  const d = new Date(ts);
  if (giorno === 'oggi') return d.toLocaleTimeString(lingua, { hour: '2-digit', minute: '2-digit' });
  if (giorno === 'ieri') return ieri;
  return d.toLocaleDateString(lingua, { day: '2-digit', month: '2-digit' });
}

interface Tiro {
  id: number;
  x0: number;
  y0: number;
  base: number;
  x: number;
  attivo: boolean;
}

/**
 * Una conversazione per riga (v2). Tocco = entri; swipe = i bottoni sotto (v. `lib/qsocials/swipeRiga.ts`).
 *
 * ⚠️ Il gesto muove il DOM direttamente e NON passa da React: un ridisegno durante un gesto (il polling che
 * porta un elenco nuovo, un non letto che cambia) riporterebbe la riga a riposo sotto il dito e toglierebbe
 * di mezzo il bersaglio del click (trappola pagata col prototipo). Per questo la posizione a riposo la scrive
 * un effetto, non una prop `style`, e l'effetto non tocca niente mentre un gesto e' in corso.
 */
export const RigaConversazione = memo(function RigaConversazione({
  c,
  me,
  sfida,
  aperta,
  ignoraClick,
  onApri,
  onAzione,
  onEntra,
}: {
  c: DmConversation;
  me: string | null;
  /** La serratura del pannello «Sfida», letta UNA volta dall'elenco (non un hook per riga: v. `useSfidaDiPiattaforma`). */
  sfida: boolean;
  /** Il lato su cui e' aperta, se e' LEI la riga aperta (una alla volta: la tiene l'elenco). */
  aperta: LatoSwipe | null;
  /** Condiviso con l'elenco: il tocco che richiude un'altra riga non apre e non entra. */
  ignoraClick: MutableRefObject<boolean>;
  onApri: (id: string, lato: LatoSwipe | null) => void;
  onAzione: (c: DmConversation, azione: AzioneRiga) => void;
  onEntra: (c: DmConversation) => void;
}) {
  const { t, i18n } = useTranslation('general');
  const rigaRef = useRef<HTMLLIElement>(null);
  const contRef = useRef<HTMLDivElement>(null);
  const tiro = useRef<Tiro | null>(null);

  const azioni = azioniRiga(c, { sfida });
  const n = { destra: azioni.destra.length, sinistra: azioni.sinistra.length };
  const latoAperto = aperta !== null && n[aperta] > 0 ? aperta : null;
  const riposo = posizioneAperta(latoAperto, n);

  const posa = (x: number, lato: LatoSwipe | null) => {
    if (contRef.current) contRef.current.style.transform = `translateX(${x}px)`;
    if (rigaRef.current) rigaRef.current.dataset.lato = lato ?? '';
  };

  // La posizione a riposo, fuori da un gesto.
  useLayoutEffect(() => {
    if (tiro.current?.attivo) return;
    posa(riposo, latoAperto);
  }, [riposo, latoAperto]);

  const larghezza = () => rigaRef.current?.clientWidth ?? 0;

  const giu = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Il tocco che ha appena richiuso un'altra riga non comincia niente (l'elenco ha gia' deciso).
    if (ignoraClick.current) return;
    tiro.current = { id: e.pointerId, x0: e.clientX, y0: e.clientY, base: riposo, x: riposo, attivo: false };
  };

  const muovi = (e: ReactPointerEvent<HTMLDivElement>) => {
    const g = tiro.current;
    if (!g || e.pointerId !== g.id) return;
    const dx = e.clientX - g.x0;
    const dy = e.clientY - g.y0;
    if (!g.attivo) {
      if (!eSwipe(dx, dy)) return;
      g.attivo = true;
      if (contRef.current) contRef.current.style.transition = 'none';
      try {
        contRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* non tutti i puntatori */
      }
    }
    const w = larghezza();
    const x = spostamentoConsentito(g.base + dx, w, n);
    g.x = x;
    posa(x, latoDi(x));
    const lungo = Math.abs(x) > w * FRAZIONE_LUNGO;
    rigaRef.current?.querySelectorAll<HTMLElement>('[data-principale]').forEach((b) => {
      b.dataset.pronta = lungo ? 'true' : '';
    });
  };

  const fine = (e: ReactPointerEvent<HTMLDivElement>) => {
    const g = tiro.current;
    if (!g || e.pointerId !== g.id) return;
    tiro.current = null;
    if (!g.attivo) return; // era un tocco: ci pensa il click
    // Il click che segue un trascinamento non entra nella conversazione.
    ignoraClick.current = true;
    if (contRef.current) contRef.current.style.transition = TRANSIZIONE;
    rigaRef.current?.querySelectorAll<HTMLElement>('[data-principale]').forEach((b) => {
      b.dataset.pronta = '';
    });
    const esito = esitoGesto(g.x, larghezza(), n);
    if (esito.tipo === 'apri') {
      posa(posizioneAperta(esito.lato, n), esito.lato);
      onApri(c.id, esito.lato);
      return;
    }
    posa(0, null);
    onApri(c.id, null);
    if (esito.tipo === 'principale') {
      const a = principale(esito.lato, azioni[esito.lato]);
      if (a) onAzione(c, a);
    }
  };

  const click = () => {
    if (ignoraClick.current) {
      ignoraClick.current = false;
      return;
    }
    // Riga aperta: il tocco la richiude soltanto.
    if (latoAperto !== null) {
      onApri(c.id, null);
      return;
    }
    onEntra(c);
  };

  const nome = c.displayName ?? c.friendUserId;
  const chiusa = c.closed === true;
  const nonLetta = !chiusa && c.unread > 0;
  const anteprima = c.lastMessage
    ? c.lastMessage.senderId === me
      ? t('social.dm.tu', { text: c.lastMessage.text })
      : c.lastMessage.text
    : '';

  const vassoio = (lato: LatoSwipe) => {
    const lista = azioni[lato];
    const prim = principale(lato, lista);
    return (
      <div
        data-vassoio={lato}
        // Si vede solo il vassoio del lato che si tira: dito a destra ⇒ i bottoni sul bordo SINISTRO.
        className={cn(
          'absolute inset-y-0 flex',
          lato === 'destra' ? 'left-0' : 'right-0',
          lato === 'destra' ? 'group-data-[lato=destra]/riga:visible' : 'group-data-[lato=sinistra]/riga:visible',
          'invisible',
        )}
      >
        {lista.map((a) => {
          const Icona = ICONA[a];
          return (
            <button
              key={a}
              type="button"
              data-testid={`dm-azione-${a}`}
              data-principale={a === prim ? 'true' : undefined}
              onClick={() => {
                onApri(c.id, null);
                onAzione(c, a);
              }}
              style={{ width: LARGHEZZA_AZIONE_PX }}
              className={cn(
                'flex flex-col items-center justify-center gap-1 px-1 text-center text-[11.5px] font-extrabold leading-tight text-white data-[pronta=true]:brightness-125',
                COLORE[a],
              )}
            >
              <Icona aria-hidden className="h-5 w-5" />
              {t(`social.dm.riga.${a}`)}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <li
      ref={rigaRef}
      data-testid="dm-riga"
      data-riga-id={c.id}
      data-chiusa={chiusa ? 'true' : undefined}
      data-nonletta={nonLetta ? 'true' : undefined}
      className="group/riga relative overflow-hidden bg-bg"
    >
      {vassoio('destra')}
      {vassoio('sinistra')}
      <div
        ref={contRef}
        role="button"
        tabIndex={0}
        data-testid="dm-riga-contenuto"
        onPointerDown={giu}
        onPointerMove={muovi}
        onPointerUp={fine}
        onPointerCancel={fine}
        onClick={click}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            click();
          }
        }}
        style={{ transition: TRANSIZIONE, touchAction: 'pan-y' }}
        className="relative z-[2] flex cursor-pointer select-none items-center gap-3 bg-bg px-4 py-2.5"
      >
        <span className={cn('shrink-0', chiusa && 'opacity-55')}>
          <UserAvatar userId={c.friendUserId} label={nome} avatarUrl={c.avatarUrl ?? undefined} size="lg" />
        </span>
        <span className={cn('min-w-0 flex-1', chiusa && 'opacity-55')}>
          <span className={cn('block truncate text-base text-fg', nonLetta ? 'font-extrabold' : 'font-bold')} {...OSCURA}>
            {nome}
          </span>
          {chiusa ? (
            <span className="flex items-center gap-1 text-sm text-fg-muted">
              <Lock aria-hidden className="h-3.5 w-3.5" />
              {t('social.dm.chiusaRiga')}
            </span>
          ) : (
            <span className={cn('block truncate text-sm', nonLetta ? 'font-extrabold text-fg' : 'text-fg-muted')} {...OSCURA}>
              {anteprima}
            </span>
          )}
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1.5 self-stretch justify-center">
          <span className={cn('text-xs', nonLetta ? 'font-bold text-brand' : 'text-fg-muted')}>
            {chiusa || !c.lastMessage ? '' : oraRiga(c.lastMessage.ts, linguaFormato(i18n), t('social.dm.ieri'))}
          </span>
          {nonLetta ? (
            <span className="grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1.5 text-[11px] font-extrabold text-brand-fg">
              {c.unread}
            </span>
          ) : (
            <span aria-hidden className="h-5" />
          )}
        </span>
      </div>
    </li>
  );
});
